import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Grid } from "@mui/material";

import { CompanyLegalEntitiesApiService } from "../../../services/company-legal-entities";
import { DocumentStatusesApiService } from "../../../services/document-statuses/";
import { DocumentCategoriesApiService } from "../../../services/document-categories";
import { DocumentTypesApiService } from "../../../services/document-types";
import { EmployeesApiService } from "../../../services/employees/employees-api.service";

import { PropsFilter } from "../../../models/api/props-filter/props-filter";

import AutocompleteDropdown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import CustomDatePicker from "../../CustomDatePicker/CustomDatePicker";

import { isValidDate } from "../../../utils/time-utils";

import { Props } from "./interfaces";
import { RootContainer, Dropdown, TextField } from "./styles";
import { ALL_LEGISLATIONS_ID, DOCUMENTS_STATUS_TYPES } from "../../../utils/constants/constants";
import { LegalEntityClientsApiService } from "../../../services/legal-entity-clients";
import { LegalEntityVendorsApiService } from "../../../services/legal-entity-vendors";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { DocumentStatusDTO } from "../../../models/documents/document-statuses";

const DocumentFormfields = ({
  document,
  isNewDocument,
  handleChangeDocumentField,
  changeDateOrPeriodOnly = false,
  readOnlyFields = {
    isLegalEntityRO: false,
    isCategoryRO: false,
    isTypeRO: false,
    isEmployeeRO: false,
    isDateRO: false,
    isStatusRO: false,
    isPeriodRO: false,
  },
  hideReadOnlyFields,
  auditTypeIds = [],
  propsFilterForLegalentity,
  customURLForLegalEntity,
  allowModifyEmployee,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.autocomplete_dropdown.add_new_document"; // tRP - translation root path

  const legalEntityApiService = new CompanyLegalEntitiesApiService();
  if (customURLForLegalEntity) {
    legalEntityApiService.useLoadItemsPath(customURLForLegalEntity);
  }

  const formFields = [
    {
      labelKey: `${tRP}.what_legal_entity_is_it_for`,
      field: "legalEntity",
      apiService: legalEntityApiService,
      selectedValue: document?.legalEntity,
      readOnly: changeDateOrPeriodOnly || (readOnlyFields.isLegalEntityRO && document?.legalEntity) ? true : false,
      hidden:
        readOnlyFields.isLegalEntityRO &&
        // document?.legalEntity &&
        hideReadOnlyFields
          ? true
          : false,
      propsFilter: { ...propsFilterForLegalentity },
    },
    {
      fieldType: "datePicker",
      // can be one of "period" or "documentDate"
      field: document?.type?.dateInsteadPeriod ?? false ? "documentDate" : "period",
      readOnly:
        (readOnlyFields.isDateRO && !!document?.documentDate) || (readOnlyFields.isPeriodRO && !!document?.period)
          ? true
          : false,
      hidden:
        ((readOnlyFields.isDateRO && document?.documentDate) || (readOnlyFields.isPeriodRO && !!document?.period)) &&
        hideReadOnlyFields
          ? true
          : false,
    },
    {
      labelKey: `${tRP}.what_type_of_document_is_it`,
      field: "category",
      apiService: new DocumentCategoriesApiService(),
      selectedValue: document?.category,
      required: true,
      readOnly: changeDateOrPeriodOnly || (readOnlyFields.isCategoryRO && document?.category?.id) ? true : false,
      hidden: readOnlyFields.isCategoryRO && document?.category?.id && hideReadOnlyFields ? true : false,
    },
    {
      labelKey: `${tRP}.what_is_document_sub-type`,
      field: "type",
      apiService: new DocumentTypesApiService(),
      selectedValue:
        document?.category && document?.type?.category?.id === document?.category?.id ? document?.type : null, // clean doc type if it was there
      hideIf: !document?.category,
      propsFilter: {
        categories: document?.category ? [document.category.id] : [],
        legislations:
          document?.legalEntity && document?.legalEntity?.legislation
            ? [document?.legalEntity?.legislation?.id, ALL_LEGISLATIONS_ID]
            : [],
        auditTypeIds: auditTypeIds,
      } as PropsFilter,
      required: true,
      readOnly: changeDateOrPeriodOnly || (readOnlyFields.isTypeRO && document?.type?.id) ? true : false,
      hidden: readOnlyFields.isTypeRO && document?.type?.id && hideReadOnlyFields ? true : false,
    },
    {
      labelKey: `${tRP}.what_employee_is_it_for`,
      field: "employee",
      apiService: new EmployeesApiService(),
      selectedValue: document?.employee,

      hideIf: !document?.legalEntity || !document?.type?.requestEeId,
      propsFilter: {
        legalEntityIds: document?.legalEntity && document?.type?.requestEeId ? [document.legalEntity.id] : [],
      } as PropsFilter,
      required: true,
      readOnly: changeDateOrPeriodOnly || (readOnlyFields.isEmployeeRO && document?.employee?.id) ? true : false,
      hidden: readOnlyFields.isEmployeeRO && document?.employee?.id && hideReadOnlyFields ? true : false,
    },
    {
      labelKey: `${tRP}.who_is_client`,
      field: "client",
      apiService: new LegalEntityClientsApiService(),
      selectedValue: document?.legalEntity && document?.type?.requireClient ? document?.client : null,
      hideIf: !document?.legalEntity || !document?.type?.requireClient,
      propsFilter: {
        legalEntityIds: document?.legalEntity && document?.type?.requireClient ? [document.legalEntity.id] : [],
      } as PropsFilter,
      required: true,
      readOnly: changeDateOrPeriodOnly || (readOnlyFields.isClientRO && document?.client?.id) ? true : false,
      hidden: readOnlyFields.isClientRO && document?.client?.id && hideReadOnlyFields ? true : false,
    },
    {
      labelKey: `${tRP}.who_is_vendor`,
      field: "vendor",
      apiService: new LegalEntityVendorsApiService(),
      selectedValue: document?.legalEntity && document?.type?.requireVendor ? document?.vendor : null,
      hideIf: !(document?.legalEntity && document?.type?.requireVendor),
      propsFilter: {
        legalEntityIds: document?.legalEntity && document?.type?.requireVendor ? [document.legalEntity.id] : [],
      } as PropsFilter,
      required: true,
    },
    {
      labelKey: `${tRP}.status`,
      field: "status",
      hideIf: isNewDocument,
      editModeOnly: true,
      apiService: new DocumentStatusesApiService(),
      selectedValue: document?.status,
      required: true,
      readOnly: changeDateOrPeriodOnly || (readOnlyFields.isStatusRO && document?.status) ? true : false,
      hidden: readOnlyFields.isStatusRO && document?.status && hideReadOnlyFields ? true : false,
    },
  ];

  return (
    <RootContainer>
      {formFields.map(
        ({
          labelKey,
          field,
          fieldType,
          selectedValue,
          apiService,
          propsFilter,
          hideIf,
          editModeOnly,
          required,
          readOnly,
          hidden,
        }) => {
          if (hidden) {
            return null;
          } else if (field === "category") {
            return (
              apiService &&
              !hideIf && (
                <Dropdown key={field}>
                  <AutocompleteDropdown
                    labelKey={labelKey ?? ""}
                    field={field}
                    selectedValue={selectedValue}
                    serviceApi={apiService}
                    propsFilter={propsFilter}
                    propsItem={document}
                    handleChange={handleChangeDocumentField}
                    required={required}
                    disabled={readOnly}
                  />
                </Dropdown>
              )
            );
          } else if (field === "legalEntity" || field === "type") {
            return (
              apiService &&
              !hideIf && (
                <Dropdown key={field}>
                  <AutocompleteDropdown
                    labelKey={labelKey ?? ""}
                    field={field}
                    selectedValue={selectedValue}
                    serviceApi={apiService}
                    propsFilter={propsFilter}
                    propsItem={document}
                    handleChange={handleChangeDocumentField}
                    required={required}
                    disabled={readOnly}
                  />
                </Dropdown>
              )
            );
          } else if (fieldType === "datePicker" && field === "period") {
            return (
              !hideIf &&
              !!document?.type && (
                <Grid
                  style={{ width: "45%" }}
                  key={field}
                >
                  <CustomDatePicker
                    handlePeriodChange={(period) => handleChangeDocumentField(field, period)}
                    period={document?.period}
                  />
                </Grid>
              )
            );
          } else if (fieldType === "datePicker" && field === "documentDate") {
            return (
              !hideIf &&
              !!document?.type && (
                <DesktopDatePicker
                  key={field}
                  inputFormat="dd.MM.yyyy"
                  mask="__.__.____"
                  label={`${t(`${tRP}.date`)}`}
                  value={document?.documentDate}
                  onChange={(newValue) => {
                    if (isValidDate(newValue)) {
                      handleChangeDocumentField(field, newValue ?? null);
                    }
                  }}
                  disabled={readOnly}
                  renderInput={(params) => <TextField {...params} />}
                />
              )
            );
          } else if (field === "status" && editModeOnly) {
            return (
              apiService &&
              !hideIf && (
                <Dropdown key={field}>
                  <AutocompleteDropdown
                    labelKey={labelKey ?? ""}
                    field={field}
                    selectedValue={selectedValue}
                    serviceApi={apiService}
                    propsFilter={propsFilter}
                    propsItem={document}
                    handleChange={handleChangeDocumentField}
                    required={required}
                    disabled={readOnly}
                    disableOption={(option: DocumentStatusDTO) =>
                      !document?.type &&
                      (option?.id === DOCUMENTS_STATUS_TYPES.VERIFIED.id ||
                        option?.id === DOCUMENTS_STATUS_TYPES.PROCESSED.id)
                    }
                  />
                </Dropdown>
              )
            );
          } else if (field === "employee") {
            return (
              apiService &&
              !hideIf && (
                <Dropdown key={field}>
                  <AutocompleteDropdown
                    labelKey={labelKey ?? ""}
                    field={field}
                    selectedValue={selectedValue}
                    serviceApi={apiService}
                    propsFilter={propsFilter}
                    propsItem={document}
                    handleChange={handleChangeDocumentField}
                    disabled={(!isNewDocument && !allowModifyEmployee) || readOnly}
                    required={required}
                  />
                </Dropdown>
              )
            );
          } else if (field === "client") {
            return (
              apiService &&
              !hideIf && (
                <Dropdown key={field}>
                  <AutocompleteDropdown
                    labelKey={labelKey ?? ""}
                    field={field}
                    selectedValue={selectedValue}
                    serviceApi={apiService}
                    propsFilter={propsFilter}
                    propsItem={document}
                    handleChange={handleChangeDocumentField}
                    disabled={readOnly}
                    required={required}
                  />
                </Dropdown>
              )
            );
          } else if (field === "vendor") {
            return (
              apiService &&
              !hideIf && (
                <Dropdown key={field}>
                  <AutocompleteDropdown
                    labelKey={labelKey ?? ""}
                    field={field}
                    selectedValue={selectedValue}
                    serviceApi={apiService}
                    propsFilter={propsFilter}
                    propsItem={document}
                    handleChange={handleChangeDocumentField}
                    disabled={readOnly}
                    required={required}
                  />
                </Dropdown>
              )
            );
          } else {
            return null;
          }
        }
      )}
    </RootContainer>
  );
};

export default DocumentFormfields;
